import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@bbpos/ui';
import type { UsageMetricSnapshot } from '@bbpos/types';
import { tenantService } from '@/services/tenant.service';
import { GLASS_PANEL, cn, toneClasses } from '@/components/features/subscriptions/design-tokens';
import {
  USAGE_METRIC_LABELS,
  formatBytes,
  usageTone,
} from '@/components/features/subscriptions/plan-entitlements';

const BAR_FILL: Record<ReturnType<typeof usageTone>, string> = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
};

function formatAmount(metric: UsageMetricSnapshot['metric'], n: number): string {
  return metric === 'storage_bytes' ? formatBytes(n) : n.toLocaleString('en-IN');
}

/** "October 2026" from the API's `2026-10`. */
function periodLabel(period: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function UsageBar({ snapshot }: { snapshot: UsageMetricSnapshot }) {
  const label = USAGE_METRIC_LABELS[snapshot.metric] ?? snapshot.metric;
  const unlimited = snapshot.limit === null;
  const tone = usageTone(snapshot.percent);
  // Usage can sit above a limit lowered later; clamp the bar, not the numbers.
  const width = unlimited ? 0 : Math.min(snapshot.percent ?? 0, 100);

  return (
    <div className="rounded-xl border border-neutral-100 dark:border-neutral-700 p-3 transition-all duration-200 hover:shadow-md animate-slide-up">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
        {unlimited ? (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              toneClasses('neutral').pill,
            )}
          >
            Unlimited
          </span>
        ) : (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
              toneClasses(tone).pill,
            )}
          >
            {snapshot.percent}%
          </span>
        )}
      </div>
      <p className="mt-1 tabular-nums text-neutral-900 dark:text-white">
        <span className="text-lg font-bold">{formatAmount(snapshot.metric, snapshot.used)}</span>
        {snapshot.limit !== null && (
          <span className="text-sm text-neutral-400 dark:text-neutral-500">
            {' '}
            of {formatAmount(snapshot.metric, snapshot.limit)}
          </span>
        )}
      </p>
      <div className="mt-2 h-2 w-full rounded-full bg-neutral-100 dark:bg-neutral-800">
        {!unlimited && (
          <div
            role="progressbar"
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={width}
            className={cn('h-2 rounded-full transition-all duration-500', BAR_FILL[tone])}
            style={{ width: `${width}%` }}
          />
        )}
      </div>
      {snapshot.metric === 'invoices' && tone === 'danger' && (
        <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
          Bills are never blocked.
        </p>
      )}
    </div>
  );
}

/** This month's usage per metric for one business, against its effective limits. */
export function TenantUsageCard({ tenantId }: { tenantId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tenant-usage', tenantId],
    queryFn: () => tenantService.getUsage(tenantId),
  });

  if (isLoading) {
    return (
      <div className={cn(GLASS_PANEL, 'p-5 mb-6')}>
        <Skeleton className="h-5 w-40" />
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={cn(GLASS_PANEL, 'p-5 mb-6')}>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Could not load usage.</p>
      </div>
    );
  }

  // Exactly at a limit is within the plan (new ones are blocked); only past it is over.
  const over = data.metrics.filter((m) => m.limit !== null && m.used > m.limit).length;
  const atLimit = data.metrics.filter((m) => usageTone(m.percent) === 'danger').length - over;
  const near = data.metrics.filter((m) => usageTone(m.percent) === 'warning').length;

  return (
    <div
      className={cn(
        GLASS_PANEL,
        'p-5 mb-6 animate-fade-in transition-shadow duration-200 hover:shadow-lg',
      )}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-md">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
              This month
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {periodLabel(data.period)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {over > 0 && (
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-semibold',
                toneClasses('danger').pill,
              )}
            >
              {over} over limit
            </span>
          )}
          {atLimit > 0 && (
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-semibold',
                toneClasses('danger').pill,
              )}
            >
              {atLimit} at limit
            </span>
          )}
          {near > 0 && (
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-semibold',
                toneClasses('warning').pill,
              )}
            >
              {near} near limit
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {data.metrics.map((m) => (
          <UsageBar key={m.metric} snapshot={m} />
        ))}
      </div>
    </div>
  );
}
